import { buildCheckoutCartItems } from "@/lib/build-checkout-cart-items"
import { LOYALTY_POINTS_CODE } from "@/lib/loyalty-benefits"
import { roundCurrency } from "@/lib/pricing"
import type { SchedulingSlot } from "@/lib/scheduling/types"
import type { Address, Cart, Category, PaymentMethod, ServiceType } from "@/types"
import { Discount, OrderRequest } from "@/types/external"
import type { OrderTiming } from "@/types/external/order-request"
import type Profile from "@/types/profile"
import { addMinutes } from "date-fns"

type BuildOrderRequestParams = {
  serviceType: ServiceType
  /** Modality of the order; SCHEDULED requires `scheduledSlot`. */
  orderTiming?: OrderTiming
  /** Chosen slot; `readyAt` is when the order leaves the store or is picked up. */
  scheduledSlot?: SchedulingSlot | null
  /**
   * Arrival window the customer was promised, from the slots endpoint (D3).
   * Present only in the full model; without it the arrival is still derived
   * from `readyAt` plus the channel estimate, as the standard mode does.
   */
  scheduledArrival?: { start: string; end: string } | null
  /** Required for DELIVERY/TAKEOUT; undefined for INDOOR (pay-at-counter, built automatically). */
  paymentMethod?: PaymentMethod
  methodInfo?: string
  paymentMethodId?: string
  specialInstructions: string
  servicePrice: number
  /**
   * PIX Online service fee (BRL major units) when pass-through is enabled and
   * paymentMethod is PIX_ONLINE. Zero otherwise. Becomes a SERVICE_FEE line.
   */
  pixServiceFee?: number
  /** One entry per coupon allocation coupon-service returned (contract C6); summed into total.discount. */
  couponDiscounts?: Discount[]
  changeFor: number
  cart: Cart
  address?: Address
  loyaltyPoints: number
  /** Pre-composed loyalty entitlements, each with its own LOYALTY_* code/target; summed into total.discount. */
  benefitDiscounts?: Discount[]
  profile: Profile
  categories: Category[]
  merchantDocument?: string
  /** Stamped at the order level (order.Merchant.Name); the merchant backend only validates it. */
  storeName: string
  takeoutEstimateMinutes?: number
  deliveryEstimateMinutes?: number
  /** Table UUID from the QR slug; builds the INDOOR `place` wire value (core rejects an empty one). */
  tableId?: string
  /** Table's PDV/POS external code, if configured; combined as `${tableId}|${tableExternalCode}` (split on first `|`). */
  tableExternalCode?: string
  /** The customer's order-update WhatsApp choice; always sent, on unless they turned it off. */
  notifyStatusWhatsapp?: boolean
}

const DEFAULT_ESTIMATE_MINUTES = 30

function resolveEstimateMinutes(value: number | undefined): number {
  return value != null && value > 0 ? value : DEFAULT_ESTIMATE_MINUTES
}

/**
 * Door / tracking code the customer already knows: last 4 digits of their phone.
 * Never use profile.id slices (opaque UUID fragments the customer cannot recite).
 */
export function pickupCodeFromPhone(phone: string | undefined | null): string {
  const digits = (phone ?? "").replace(/\D/g, "")
  if (digits.length >= 4) return digits.slice(-4)
  if (!digits) return "0000"
  return digits.padStart(4, "0")
}

/**
 * Pure assembly of the checkout OrderRequest payload. No validation or side
 * effects — callers guard service/payment/minimum before calling this.
 */
export function buildOrderRequest({
  serviceType,
  orderTiming = "INSTANT",
  scheduledSlot = null,
  scheduledArrival = null,
  paymentMethod,
  methodInfo,
  paymentMethodId,
  specialInstructions,
  servicePrice,
  pixServiceFee = 0,
  couponDiscounts = [],
  changeFor,
  cart,
  address,
  loyaltyPoints,
  benefitDiscounts = [],
  profile,
  categories,
  merchantDocument,
  storeName,
  takeoutEstimateMinutes,
  deliveryEstimateMinutes,
  tableId,
  tableExternalCode,
  notifyStatusWhatsapp = true,
}: BuildOrderRequestParams): OrderRequest {
  const scheduled = orderTiming === "SCHEDULED" && scheduledSlot !== null
  const readyAt = scheduled ? new Date(scheduledSlot.readyAt) : null
  const arrivalAt = scheduled && scheduledArrival ? new Date(scheduledArrival.start) : null

  const benefitTotal = roundCurrency(
    benefitDiscounts.reduce((sum, d) => sum + d.amount.value, 0),
  )

  const couponTotal = roundCurrency(
    couponDiscounts.reduce((sum, d) => sum + d.amount.value, 0),
  )
  const pointsShare = roundCurrency(loyaltyPoints)

  const itemsPrice = roundCurrency(cart.totalPrice)
  const deliveryFeePrice = roundCurrency(servicePrice)
  const serviceFeePrice =
    paymentMethod === "PIX_ONLINE" ? roundCurrency(Math.max(0, pixServiceFee)) : 0
  const otherFeesPrice = roundCurrency(deliveryFeePrice + serviceFeePrice)
  const totalDiscount = roundCurrency(couponTotal + pointsShare + benefitTotal)
  const totalPrice = roundCurrency(Math.max(0, itemsPrice + otherFeesPrice - totalDiscount))

  const discounts: Discount[] = [...couponDiscounts]
  if (pointsShare > 0) {
    discounts.push({
      amount: {
        value: pointsShare,
        currency: "BRL"
      },
      target: "CART",
      sponsorshipValues: [{
        name: "MERCHANT",
        amount: {
          value: pointsShare,
          currency: "BRL"
        },
        discountCode: LOYALTY_POINTS_CODE
      }]
    })
  }
  discounts.push(...benefitDiscounts)

  if (serviceType === "INDOOR") {
    const place = tableExternalCode
      ? `${tableId ?? ""}|${tableExternalCode}`
      : (tableId ?? "")

    return {
      type: "INDOOR",
      discounts: [],
      items: buildCheckoutCartItems(cart, categories),
      otherFees: [],
      total: {
        otherFees:   { value: 0,          currency: "BRL" },
        discount:    { value: 0,          currency: "BRL" },
        itemsPrice:  { value: itemsPrice, currency: "BRL" },
        orderAmount: { value: itemsPrice, currency: "BRL" },
      },
      payments: {
        prepaid: 0,
        pending: itemsPrice,
        methods: [{
          type:       "PENDING",
          method:     "OTHER",
          brand:      "OTHER",
          methodInfo: "pay_at_counter",
          value:      itemsPrice,
          currency:   "BRL",
        }],
      },
      indoor: {
        mode:            "PLACE",
        place:           place,
        indoorDateTime:  new Date().toISOString(),
      },
      extraInfo:     specialInstructions,
      storeName:     storeName,
      orderTiming:   "INSTANT",
      orderPriority: "PRIORITY1",
      notifyStatusWhatsapp,
    }
  }

  const pm = paymentMethod as PaymentMethod

  const otherFees: OrderRequest["otherFees"] = []
  if (serviceFeePrice > 0) {
    otherFees.push({
      type: "SERVICE_FEE",
      name: "SERVICE",
      receivedBy: "MARKETPLACE",
      receiverDocument: merchantDocument as string,
      price: {
        value: serviceFeePrice,
        currency: "BRL",
      },
      observation: "Taxa de serviço",
    })
  }
  if (serviceType === "DELIVERY") {
    otherFees.push({
      type: "DELIVERY_FEE",
      name: "DELIVERY",
      receivedBy: "MERCHANT",
      receiverDocument: merchantDocument as string,
      price: {
        value: deliveryFeePrice,
        currency: "BRL",
      },
      observation: "Taxa de entrega",
    })
  }

  const orderRequest: OrderRequest = {
    type: serviceType,
    discounts: discounts,
    items: buildCheckoutCartItems(cart, categories),
    otherFees,
    total: {
      otherFees: {
        value: otherFeesPrice,
        currency: "BRL"
      },
      discount: {
        value: totalDiscount,
        currency: "BRL"
      },
      itemsPrice: {
        value: itemsPrice,
        currency: "BRL"
      },
      orderAmount: {
        value: totalPrice,
        currency: "BRL"
      }
    },
    payments: {
      prepaid: 0,
      pending: totalPrice,
      methods: [{
        currency: "BRL",
        value: totalPrice,
        type: "PENDING",
        brand: "OTHER",
        method: pm === "PIX_ONLINE" ? "PIX" : pm === "CARD" ? "CREDIT" : pm,
        methodInfo: pm === "PIX_ONLINE" ? "PIX_ONLINE" : methodInfo,
        paymentMethodId: paymentMethodId,
        changeFor: changeFor,
      }]
    },
    extraInfo: specialInstructions,
    storeName: storeName,
    orderTiming: scheduled ? "SCHEDULED" : "INSTANT",
    orderPriority: "PRIORITY1",
    notifyStatusWhatsapp,
  }

  if (scheduled && scheduledSlot && readyAt) {
    orderRequest.schedule = {
      scheduledDateTimeStart: readyAt.toISOString(),
      scheduledDateTimeEnd: new Date(scheduledSlot.readyEndAt).toISOString(),
    }
  }

  if (serviceType === "DELIVERY" && address) {
    orderRequest.delivery = {
      deliveredBy: "MERCHANT",
      deliveryAddress: {
        city: address.city,
        state: address.state,
        number: address.number,
        street: address.street,
        country: address.country,
        district: address.district,
        postalCode: address.postalCode,
        reference: address.reference,
        complement: address.complement,
        formattedAddress: "",
        coordinates: {
          latitude: address.latitude,
          longitude: address.longitude
        }
      },
      estimatedDeliveryDateTime:
        arrivalAt ??
        addMinutes(readyAt ?? Date.now(), resolveEstimateMinutes(deliveryEstimateMinutes)),
      pickupCode: pickupCodeFromPhone(profile.phone?.number)
    }
  } else {
    orderRequest.takeout = {
      mode: "DEFAULT",
      takeoutDateTime:
        arrivalAt ??
        readyAt ??
        addMinutes(Date.now(), resolveEstimateMinutes(takeoutEstimateMinutes)),
    }
  }

  return orderRequest
}
